import { useMemo, useSyncExternalStore } from "react"

import { useRepeatScope } from "../dashboard/repeat-scope"
import { useDashboardStateStore } from "../state/dashboard-state"
import { getAllComputedFunctions } from "../utils/computed-functions"
import { createDirectiveRegistry } from "../utils/directives"
import {
  collectStatePaths,
  type ResolveContext,
  resolveElement,
  resolvePropValue,
} from "../utils/expressions"
import { standardDirectives } from "../utils/standard-directives"

// Module-level registry — every dashboard renderer shares the same
// standard directives ($format, $math, $truncate, etc.). Custom host
// directives (e.g. an i18n `$t`) can be added through a future
// DirectiveRegistryProvider; for now the standard set is enough.
const STANDARD_REGISTRY = createDirectiveRegistry(standardDirectives)

/**
 * Hash of the state slices an element references — a primitive value
 * useMemo can compare against. Recomputed on every render but cheap:
 * for each referenced path, generates an identity key based on the
 * value's reference (or a primitive's stringification) — never
 * stringifies arrays or large objects.
 *
 * Why §D-original used `JSON.stringify` and we no longer do: the
 * state store does immutable updates, so an unchanged path returns
 * the SAME reference between renders. Reference-based identityKey
 * is O(1) per path; the prior stringify was O(value-size). For a
 * dashboard with a 10k-row /data array referenced by Repeat, this
 * change drops sliceKey cost from O(rows) to O(1) per render.
 */
function sliceKey(paths: Set<string>, store: { getDashState: (path: string) => unknown }): string {
  if (paths.size === 0) return ""
  // Sort for stability (Set iteration order tracks insertion in
  // modern engines, so different traversal orders for similarly-shaped
  // elements would otherwise yield different keys).
  const sorted = [...paths].sort()
  let key = ""
  for (const p of sorted) {
    // identityKey: O(1) for primitives + WeakMap-backed counter for
    // objects/arrays. Immutable state updates produce new refs for
    // changed paths only, so the counter naturally tracks "did this
    // slice change" without paying the size of the underlying value.
    key += p + "=" + identityKey(store.getDashState(p)) + " "
  }
  return key
}

/**
 * Resolves all expression props ($state, $template, $filter, $computed, $cond,
 * plus directives like $format/$math/$truncate) in a dashboard element
 * against the current dashboard state. Re-renders whenever dashboard
 * state changes. Inside a Repeat body, also threads $item/$index from
 * the surrounding RepeatScopeProvider into the resolve context.
 *
 * Slice-aware memoisation (§D): `useMemo` re-runs only when the values
 * at the state paths this element references change — not on every
 * unrelated state write.
 *
 * Note on the `store` dep below: kept intentionally so that swapping
 * the state store entirely (via the §2.2 pluggable adapter wired
 * onto DashboardStateProvider) busts every cached resolution. That's
 * the correct semantic — the underlying data source changed, prior
 * results don't apply. Removing `store` would silently return stale
 * data from the previous store after a swap.
 */
export function useResolvedElement(element: Record<string, unknown>): Record<string, unknown> {
  const store = useDashboardStateStore()
  const repeatScope = useRepeatScope()

  // Subscribe so we re-render on any state change. The aggressive
  // re-render is fine — useMemo below filters down to the paths the
  // element actually reads.
  useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)

  // Walk the element once per identity change to find which state
  // paths it reads. Memoised by element reference.
  const referencedPaths = useMemo(() => collectStatePaths(element), [element])

  // Stable primitive that captures the *content* at those paths.
  // useMemo on a primitive is a value comparison, so unrelated state
  // writes don't bust the resolution cache.
  const key = sliceKey(referencedPaths, store)

  // Repeat scope identity also affects resolution (changes $item/$index
  // outputs); include item identity + index in the dep set.
  const itemKey = repeatScope ? `${repeatScope.index}:${identityKey(repeatScope.item)}` : "-"

  return useMemo(() => {
    const ctx: ResolveContext = {
      getState: (path: string) => store.getDashState(path),
      functions: getAllComputedFunctions() as Record<string, (...args: unknown[]) => unknown>,
      directives: STANDARD_REGISTRY,
      repeatItem: repeatScope?.item,
      repeatIndex: repeatScope?.index,
    }
    return resolveElement(element, ctx)
  }, [element, key, itemKey, store])
}

/**
 * Like useResolvedElement but resolves a *single* expression — used by
 * the Repeat element to evaluate its `source` prop against the current
 * state (without a repeat scope, since Repeat itself is the *source*
 * of one).
 */
export function useResolvedValue(value: unknown): unknown {
  const store = useDashboardStateStore()
  const repeatScope = useRepeatScope()
  useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)

  const referencedPaths = useMemo(() => collectStatePaths(value), [value])
  const key = sliceKey(referencedPaths, store)
  const itemKey = repeatScope ? `${repeatScope.index}:${identityKey(repeatScope.item)}` : "-"

  return useMemo(() => {
    const ctx: ResolveContext = {
      getState: (path: string) => store.getDashState(path),
      functions: getAllComputedFunctions() as Record<string, (...args: unknown[]) => unknown>,
      directives: STANDARD_REGISTRY,
      repeatItem: repeatScope?.item,
      repeatIndex: repeatScope?.index,
    }
    return resolvePropValue(value, ctx)
  }, [value, key, itemKey, store])
}

/**
 * Cheap stable-ish identity for arbitrary values. Primitives stringify
 * to a typed string; objects/arrays get a WeakMap-backed counter so
 * two distinct refs with identical content still produce different
 * keys (matches our "immutable update produces new ref" invariant).
 *
 * Used by `sliceKey` for state-slice change detection AND for repeat-
 * scope item identity.
 */
const identityCounter = new WeakMap<object, number>()
let nextIdentity = 1
function identityKey(value: unknown): string {
  if (value === null || value === undefined) return String(value)
  if (typeof value !== "object") return `${typeof value}:${String(value)}`
  let id = identityCounter.get(value as object)
  if (id === undefined) {
    id = nextIdentity++
    identityCounter.set(value as object, id)
  }
  return `o:${id}`
}
